import { useEffect, useMemo, useState } from "react";
import {
  fetchConfigurableConcepts,
  normalizePlanTypeApiCode,
  type ConfigurableConcept,
  type ConfigurableConceptsKind,
} from "../services/autoTaggingService";

export type ConceptFetcher = (
  targetType: string,
  subType?: string | null,
  kind?: ConfigurableConceptsKind | null,
) => Promise<ConfigurableConcept[]>;

export interface UseConfigurableConceptsResult {
  concepts: ConfigurableConcept[];
  conceptOptions: ConfigurableConcept[];
  loading: boolean;
  error: string | null;
}

function normalizeTargetTypes(
  targetTypeOrTypes?: string | null | readonly string[],
): string[] {
  if (targetTypeOrTypes == null) return [];
  if (typeof targetTypeOrTypes !== "string") {
    const seen = new Set<string>();
    const types: string[] = [];
    for (const item of targetTypeOrTypes) {
      const code = normalizePlanTypeApiCode(item);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      types.push(code);
    }
    return types;
  }
  const single = normalizePlanTypeApiCode(targetTypeOrTypes);
  return single ? [single] : [];
}

function mergeConcepts(
  groups: readonly ConfigurableConcept[][],
): ConfigurableConcept[] {
  const seen = new Set<string>();
  const merged: ConfigurableConcept[] = [];
  for (const items of groups) {
    for (const item of items) {
      const code = item.code.trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      merged.push(item);
    }
  }
  return merged;
}

function isConceptFetcher(value: unknown): value is ConceptFetcher {
  return typeof value === "function";
}

export type UseConfigurableConceptsLoadOptions = {
  /**
   * When false, skip the network request (e.g. cascade popover closed).
   * Default true.
   */
  enabled?: boolean;
  /**
   * Bump to force a refetch (e.g. every popover open for fresh options).
   * Identity changes only — value is not sent to the API.
   */
  reloadToken?: number;
};

function normalizeLoadOptions(
  reloadTokenOrOptions?: number | UseConfigurableConceptsLoadOptions,
): Required<UseConfigurableConceptsLoadOptions> {
  if (typeof reloadTokenOrOptions === "number") {
    return { enabled: true, reloadToken: reloadTokenOrOptions };
  }
  return {
    enabled: reloadTokenOrOptions?.enabled !== false,
    reloadToken: reloadTokenOrOptions?.reloadToken ?? 0,
  };
}

/**
 * Loads concepts for one or more API `target_type` values.
 * Pass an array (e.g. all object types) to merge options for filter "All".
 * Second argument may be a `subType` or a custom fetcher (output-concepts).
 * Third argument is configurable-concepts `kind` (`impacted` | `reference` |
 * `assessment_record`); omitted → backend default `impacted`.
 * Fourth argument may be a `reloadToken` number or `{ enabled, reloadToken }`.
 */
export function useConfigurableConcepts(
  targetTypeOrTypes?: string | null | readonly string[],
  fetchConceptsOrSubType?: ConceptFetcher | string | null,
  kind?: ConfigurableConceptsKind | null,
  reloadTokenOrOptions?: number | UseConfigurableConceptsLoadOptions,
): UseConfigurableConceptsResult {
  const fetchConcepts = isConceptFetcher(fetchConceptsOrSubType)
    ? fetchConceptsOrSubType
    : fetchConfigurableConcepts;
  const subType = isConceptFetcher(fetchConceptsOrSubType)
    ? undefined
    : fetchConceptsOrSubType;
  const { enabled, reloadToken } = normalizeLoadOptions(reloadTokenOrOptions);
  const [concepts, setConcepts] = useState<ConfigurableConcept[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const targetTypes = useMemo(
    () => normalizeTargetTypes(targetTypeOrTypes),
    [targetTypeOrTypes],
  );
  const targetTypesKey = targetTypes.join("|");
  const subTypeCode = normalizePlanTypeApiCode(subType ?? "");
  const kindCode = (kind ?? "").trim();

  useEffect(() => {
    let mounted = true;

    if (!enabled) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    if (targetTypes.length === 0) {
      setConcepts([]);
      setError(null);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);

    const kindArg =
      kindCode === "impacted" ||
      kindCode === "reference" ||
      kindCode === "assessment_record"
        ? (kindCode as ConfigurableConceptsKind)
        : null;

    Promise.all(
      targetTypes.map((type) =>
        fetchConcepts(type, subTypeCode || null, kindArg),
      ),
    )
      .then((groups) => {
        if (!mounted) return;
        setConcepts(mergeConcepts(groups));
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setConcepts([]);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load configurable concepts",
        );
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
    // targetTypesKey is a stable serialization of targetTypes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefer key over array identity
  }, [targetTypesKey, fetchConcepts, subTypeCode, kindCode, enabled, reloadToken]);

  const conceptOptions = useMemo(() => concepts, [concepts]);

  return {
    concepts,
    conceptOptions,
    loading,
    error,
  };
}
